/* eslint-disable no-nested-ternary */
/* eslint-disable react/jsx-no-useless-fragment */
import {
  Autocomplete,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import styles from "./internalPayment.module.scss";
import { Wallet } from "src/page/wallets/walletSlice";
import { useAppDispatch, useAppSelector } from "src/page/store";
import {
  fetchUserListAction,
  fetchWalletListAction,
  internalPayAction,
  resetAllWalletAction,
  resetwalletListAction,
  fetchGroupListAction,
  fetchGroupDetailAction
} from "src/page/newPayment/newPaymentSlice";
import { useCallback, useEffect, useState } from "react";
import { debounce } from "lodash";
import Modalui from "../UI/modal/Modalui";
import { Users } from "src/page/users/usersSlice";
import {
  CAN_TRANSFER_GROUP_TO_GROUP,
  CAN_TRANSFER_GROUP_TO_PARTICIPANT,
  CAN_TRANSFER_PARTICIPANT_TO_GROUP,
  CAN_TRANSFER_PARTICIPANT_TO_PARTICIPANT,
  EMPTY_STRING
} from "../constant/constant";
import { Group } from "src/page/group/groupSlice";
import { GroupWallet } from "src/page/groupDetail/groupDetailSlice";
import { checkPermission, getDecimalNumber } from "src/utils/utils";

interface InternalPaymentProp {
  onClose?: () => void;
}

interface ErrorState {
  transferType: string;
  sourceParticipant: string;
  sourceWallet: string;
  destinationParticipant: string;
  destinationWallet: string;
  sourceGroup: string;
  destinationGroup: string;
  amount: string;
  [key: string]: string;
}

export default function InternalPay({ onClose }: InternalPaymentProp) {
  const dispatch = useAppDispatch();
  const {
    wallets,
    users,
    internalPayLoading,
    userLoading,
    walletLoading,
    groups,
    groupDetail,
    groupDetailLoading,
    groupsLoading
  } = useAppSelector((state) => state.newPayment);
  const [open, setOpen] = useState<boolean>(false);
  const [transferType, setTransferType] = useState<string>(EMPTY_STRING);
  const [sourceParticipant, setSourceParticipant] = useState<Users | null>(null);
  const [destinationParticipant, setDestinationParticipant] = useState<Users | null>(null);
  const [sourceWallet, setSourceWallet] = useState<Wallet | GroupWallet | null>(null);
  const [destinationWallet, setDestinationWallet] = useState<Wallet | GroupWallet | null>(null);
  const [sourceGroup, setSourceGroup] = useState<Group | null>(null);
  const [destinationGroup, setDestinationGroup] = useState<Group | null>(null);
  const [amount, setAmount] = useState(EMPTY_STRING);
  const [error, setError] = useState<ErrorState>({
    transferType: EMPTY_STRING,
    sourceParticipant: EMPTY_STRING,
    sourceWallet: EMPTY_STRING,
    destinationParticipant: EMPTY_STRING,
    destinationWallet: EMPTY_STRING,
    sourceGroup: EMPTY_STRING,
    destinationGroup: EMPTY_STRING,
    amount: EMPTY_STRING
  });

  const resetError = () => {
    setError({
      transferType: EMPTY_STRING,
      sourceParticipant: EMPTY_STRING,
      sourceWallet: EMPTY_STRING,
      destinationParticipant: EMPTY_STRING,
      destinationWallet: EMPTY_STRING,
      sourceGroup: EMPTY_STRING,
      destinationGroup: EMPTY_STRING,
      amount: EMPTY_STRING
    });
  };

  const handleClose = () => setOpen(false);

  const validateAmount = () => {
    const newError = { ...error };
    const numericAmount = Number(amount);

    if (numericAmount <= 0) {
      newError.amount = "Amount can't be less than or equal to 0";
    } else if (sourceWallet && numericAmount > sourceWallet.balance) {
      newError.amount = `Amount cannot be more than $${sourceWallet.balance}`;
    } else if (numericAmount > 1000) {
      newError.amount = "Amount cannot be more than $1000";
    } else {
      newError.amount = EMPTY_STRING;
    }

    setError(newError);
    return newError.amount === EMPTY_STRING;
  };

  const fetchUserList = useCallback(() => {
    dispatch(fetchUserListAction({ size: 20, from: 0 }));
  }, [dispatch]);

  const fetchGroupList = useCallback(() => {
    dispatch(fetchGroupListAction({ size: 100, from: 0, filter: {} }));
  }, [dispatch]);

  useEffect(() => {
    fetchUserList();
    fetchGroupList();
  }, []);

  const fetchWalletList = useCallback(
    (userId: string) => {
      dispatch(
        fetchWalletListAction({
          walletFilter: { wallet_types: ["GENERAL"], user_ids: [String(userId)], wallet_status: "ACTIVE" }
        })
      );
    },
    [dispatch]
  );

  const fetchGroupDetail = useCallback(
    (groupId: string) => {
      dispatch(fetchGroupDetailAction(groupId));
    },
    [dispatch]
  );

  useEffect(() => {
    if (amount) {
      validateAmount();
    }
  }, [amount, sourceWallet]);

  useEffect(() => {
    if (sourceGroup?.group_id) {
      fetchGroupDetail(sourceGroup.group_id);
    }
  }, [sourceGroup?.group_id, fetchGroupDetail]);

  useEffect(() => {
    if (destinationGroup?.group_id) {
      fetchGroupDetail(destinationGroup.group_id);
    }
  }, [destinationGroup?.group_id, fetchGroupDetail]);

  const handleBack = () => {
    if (onClose) {
      onClose();
    }
  };

  const getWalletId = (wallet: Wallet | GroupWallet | null): string | undefined => {
    if (!wallet) return undefined;
    return ("id" in wallet ? wallet.id : wallet.wallet_id) as string;
  };

  const handleTransferFund = () => {
    const data = {
      amount: Number(amount),
      designationWalletId: String(getWalletId(destinationWallet)),
      sourceWalletId: String(getWalletId(sourceWallet))
    };

    dispatch(
      internalPayAction(data, () => {
        setSourceParticipant(null);
        setDestinationParticipant(null);
        setSourceWallet(null);
        setDestinationWallet(null);
        setSourceGroup(null);
        setDestinationGroup(null);
        setAmount(EMPTY_STRING);
        setTransferType(EMPTY_STRING);
        setOpen(false);
        dispatch(resetAllWalletAction([]));
      })
    );
  };

  const handleConfirm = () => {
    const newError: ErrorState = {
      transferType: transferType ? EMPTY_STRING : "Transfer type is required",
      sourceParticipant: EMPTY_STRING,
      sourceWallet: getWalletId(sourceWallet) ? EMPTY_STRING : "Source Wallet is required",
      destinationParticipant: EMPTY_STRING,
      destinationWallet: getWalletId(destinationWallet)
        ? getWalletId(sourceWallet) === getWalletId(destinationWallet)
          ? "From wallet and To wallet must be different"
          : EMPTY_STRING
        : "Destination Wallet is required",
      sourceGroup: EMPTY_STRING,
      destinationGroup: EMPTY_STRING,
      amount: amount.trim() ? EMPTY_STRING : "Amount is required"
    };

    if (transferType === "P2P" || transferType === "P2G") {
      newError.sourceParticipant = sourceParticipant?.userId ? EMPTY_STRING : "Source Participant is required";
      if (transferType === "P2P") {
        newError.destinationParticipant = destinationParticipant?.userId
          ? EMPTY_STRING
          : "Destination Participant is required";
      }
    }
    if (transferType === "G2G" || transferType === "G2P") {
      newError.sourceGroup = sourceGroup?.group_id ? EMPTY_STRING : "Source Group is required";
      if (transferType === "G2G") {
        newError.destinationGroup = destinationGroup?.group_id ? EMPTY_STRING : "Destination Group is required";
      }
    }

    setError(newError);

    if (
      !newError.transferType &&
      !newError.sourceParticipant &&
      !newError.sourceWallet &&
      !newError.destinationParticipant &&
      !newError.destinationWallet &&
      !newError.sourceGroup &&
      !newError.destinationGroup &&
      !newError.amount &&
      validateAmount()
    ) {
      setOpen(true);
    }
  };

  const handleUserChange = debounce((event: React.ChangeEvent<object>, input: string) => {
    if (input.length < 2) return;
    const data = {
      user_filter: {
        full_name: input
      },
      size: 15,
      from: 0
    };

    dispatch(fetchUserListAction(data));
  }, 500);

  const renderParticipantSelector = (
    label: string,
    value: Users | null,
    onChange: (_value: Users | null) => void,
    errorKey: string
  ) => (
    <>
      <Autocomplete
        options={users?.list}
        getOptionLabel={(option) => option?.firstName ?? option}
        filterOptions={(x) => x}
        value={value}
        onInputChange={handleUserChange}
        onChange={(event, newValue) => {
          onChange(newValue);
          dispatch(resetwalletListAction({ list: [] }));
          if (newValue?.userId) {
            fetchWalletList(String(newValue.userId));
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder="type to search...."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {userLoading ? (
                    <CircularProgress size={20} style={{ marginRight: 10 }} />
                  ) : (
                    params.InputProps.endAdornment
                  )}
                </>
              )
            }}
          />
        )}
        renderOption={(props, option) => (
          <li
            {...props}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start"
            }}
          >
            <span>{option?.firstName + " " + option?.lastName}</span>
            <span style={{ color: "#64748B" }}>{option?.email}</span>
          </li>
        )}
      />
      {error[errorKey] && (
        <div className={styles.error}>
          <ErrorOutlineIcon className={styles.errIcon} />
          {error[errorKey]}
        </div>
      )}
    </>
  );

  const renderWalletSelector = (
    label: string,
    value: Wallet | GroupWallet | null,
    options: (Wallet | GroupWallet)[],
    onChange: (_value: Wallet | GroupWallet | null) => void,
    errorKey: string,
    loading: boolean = false
  ) => (
    <>
      <Autocomplete
        options={options}
        filterOptions={(x) => x}
        getOptionLabel={(option) => option.name || EMPTY_STRING}
        value={value}
        onChange={(event, newValue) => {
          onChange(newValue);
          dispatch(resetwalletListAction({ list: [] }));
          setError({
            ...error,
            [errorKey]:
              newValue &&
              getWalletId(newValue) ===
                (errorKey === "sourceWallet" ? getWalletId(destinationWallet) : getWalletId(sourceWallet))
                ? "From wallet and To wallet must be different"
                : EMPTY_STRING,
            amount: EMPTY_STRING
          });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder="type to search...."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress size={20} style={{ marginRight: 10 }} />
                  ) : (
                    params.InputProps.endAdornment
                  )}
                </>
              )
            }}
          />
        )}
        renderOption={(props, option) => (
          <li
            {...props}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start"
            }}
          >
            <span>{option.name}</span>
            <span style={{ fontSize: "12px" }}>
              Balance:
              <span style={{ color: "green" }}> ${getDecimalNumber(option?.balance)}</span>
            </span>
          </li>
        )}
      />
      {error[errorKey] && (
        <div className={styles.error}>
          <ErrorOutlineIcon className={styles.errIcon} />
          {error[errorKey]}
        </div>
      )}
      {value && !error[errorKey] && <div className={styles.success}>balance: {getDecimalNumber(value.balance)}</div>}
    </>
  );

  const renderGroupSelector = (
    label: string,
    value: Group | null,
    onChange: (_value: Group | null) => void,
    errorKey: string
  ) => (
    <>
      <Autocomplete
        options={groups?.list}
        getOptionLabel={(option) => option?.name ?? option}
        value={value}
        onChange={(event, newValue) => {
          onChange(newValue);
          setError({
            ...error,
            [errorKey]: EMPTY_STRING
          });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            aria-label={label.toLowerCase()}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {groupsLoading ? (
                    <CircularProgress size={20} style={{ marginRight: 10 }} />
                  ) : (
                    params.InputProps.endAdornment
                  )}
                </>
              )
            }}
          />
        )}
        renderOption={(props, option) => (
          <li
            {...props}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start"
            }}
          >
            <span>{option?.name}</span>
            <span style={{ color: "#64748B" }}>{option?.description}</span>
          </li>
        )}
      />
      {error[errorKey] && (
        <div className={styles.error}>
          <ErrorOutlineIcon className={styles.errIcon} />
          {error[errorKey]}
        </div>
      )}
    </>
  );

  const renderFromWallet = () => {
    if (!transferType) return null;
    return (
      <div className={styles.fromWallet}>
        <h3>From Wallet</h3>
        {(transferType === "P2P" || transferType === "P2G") && (
          <>
            {renderParticipantSelector(
              "Select Participant",
              sourceParticipant,
              (value) => {
                setSourceParticipant(value);
                setSourceWallet(null);
                setDestinationGroup(null);
                setDestinationParticipant(null);
                setDestinationWallet(null);
                setAmount(EMPTY_STRING);
                resetError();
              },
              "sourceParticipant"
            )}
            {renderWalletSelector(
              "Select Wallet",
              sourceWallet,
              wallets?.list,
              setSourceWallet,
              "sourceWallet",
              walletLoading
            )}
          </>
        )}
        {(transferType === "G2G" || transferType === "G2P") && (
          <>
            {renderGroupSelector(
              "Select Group",
              sourceGroup,
              (input) => {
                setSourceGroup(input);
                setSourceWallet(null);
                setDestinationGroup(null);
                setDestinationParticipant(null);
                setDestinationWallet(null);
                setAmount(EMPTY_STRING);
                resetError();
              },
              "sourceGroup"
            )}
            {renderWalletSelector(
              "Select Wallet",
              sourceWallet,
              groupDetail?.wallets?.filter((w) => w.wallet_status === "ACTIVE") || [],
              setSourceWallet,
              "sourceWallet",
              groupDetailLoading
            )}
          </>
        )}
      </div>
    );
  };

  const renderToWallet = () => {
    if (!transferType) return null;
    return (
      <div className={styles.toWallet}>
        <h3>To Wallet</h3>
        {(transferType === "P2P" || transferType === "G2P") && (
          <>
            {renderParticipantSelector(
              "Select Participant",
              destinationParticipant,
              (input) => {
                setDestinationParticipant(input);
                setDestinationWallet(null);
              },
              "destinationParticipant"
            )}
            {renderWalletSelector(
              "Select Wallet",
              destinationWallet,
              wallets?.list,
              setDestinationWallet,
              "destinationWallet",
              walletLoading
            )}
          </>
        )}
        {(transferType === "G2G" || transferType === "P2G") && (
          <>
            {renderGroupSelector(
              "Select Group",
              destinationGroup,
              (input) => {
                setDestinationGroup(input);
                setDestinationWallet(null);
              },
              "destinationGroup"
            )}
            {renderWalletSelector(
              "Select Wallet",
              destinationWallet,
              groupDetail?.wallets?.filter((w) => w.wallet_status === "ACTIVE") || [],
              setDestinationWallet,
              "destinationWallet",
              groupDetailLoading
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <Modalui open={open} handleClose={handleClose}>
        <div className={styles.referModalBox}>
          <div className={styles.referModalTop}>
            <form>
              <div className={styles.setting}>
                <span>{`Are you sure you want to transfer`}</span>
                <span>{`$${amount} from ${sourceWallet?.name} to ${destinationWallet?.name}`}</span>
              </div>
            </form>
          </div>
          <div className={styles.referModalBottom}>
            {internalPayLoading ? (
              <div className={styles.progress}>
                <CircularProgress sx={{ color: "#0f172a" }} />
              </div>
            ) : (
              <div className={styles.referModalBtn}>
                <div className={styles.btnL}>
                  <Button className={styles.reportBtn} variant="text" onClick={handleClose}>
                    Cancel
                  </Button>
                </div>
                <div className={styles.btnR}>
                  <Button className={styles.sendBtn} variant="contained" onClick={handleTransferFund}>
                    Sure
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modalui>
      <div className={styles.internalPaymentCard} aria-label="Transfer Funds">
        <h1>Transfer Funds</h1>
        <FormControl sx={{ m: 1, margin: 0 }}>
          <InputLabel id="transfer-type-label">Transfer Between</InputLabel>
          <Select
            value={transferType}
            labelId="transfer-type-label"
            id="select-transfer-type"
            label="Transfer Between"
            onChange={(e) => {
              setTransferType(e.target.value);
              setSourceParticipant(null);
              setDestinationParticipant(null);
              setSourceWallet(null);
              setDestinationWallet(null);
              setSourceGroup(null);
              setDestinationGroup(null);
              setAmount(EMPTY_STRING);
              resetError();
            }}
            aria-label="select transfer type"
            inputProps={{ "aria-label": "Transfer Type" }}
          >
            {checkPermission(CAN_TRANSFER_PARTICIPANT_TO_PARTICIPANT) && (
              <MenuItem value="P2P">Participant Wallet to Participant Wallet</MenuItem>
            )}
            {checkPermission(CAN_TRANSFER_GROUP_TO_GROUP) && (
              <MenuItem value="G2G">Group Wallet to Group Wallet</MenuItem>
            )}
            {checkPermission(CAN_TRANSFER_PARTICIPANT_TO_GROUP) && (
              <MenuItem value="P2G">Participant Wallet to Group Wallet</MenuItem>
            )}
            {checkPermission(CAN_TRANSFER_GROUP_TO_PARTICIPANT) && (
              <MenuItem value="G2P">Group Wallet to Participant Wallet</MenuItem>
            )}
          </Select>
          {error.transferType && (
            <div className={styles.error}>
              <ErrorOutlineIcon className={styles.errIcon} />
              {error.transferType}
            </div>
          )}
        </FormControl>
        {renderFromWallet()}
        {renderToWallet()}
        {transferType && (
          <>
            <div>
              <div className={styles.amountCard} aria-live="polite">
                <div className={styles.title}>How much do you need?</div>
                <div className={styles.amount}>
                  <div className={styles.amtValue} onClick={() => setAmount("10")}>
                    $10
                  </div>
                  <div className={styles.amtValue} onClick={() => setAmount("20")}>
                    $20
                  </div>
                  <div className={styles.amtValue} onClick={() => setAmount("50")}>
                    $50
                  </div>
                  <div className={styles.amtValue} onClick={() => setAmount("100")}>
                    $100
                  </div>
                </div>
                <div className={styles.amountInput}>
                  <input
                    type="number"
                    placeholder="Enter Amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    aria-label="Enter Amount"
                  />
                </div>
              </div>
              {error.amount && (
                <div className={styles.error}>
                  <ErrorOutlineIcon className={styles.errIcon} />
                  {error.amount}
                </div>
              )}
            </div>
            <div className={styles.buttons}>
              {onClose && (
                <Button variant="contained" className={styles.backBtn} onClick={handleBack}>
                  Back
                </Button>
              )}
              <Button variant="contained" className={styles.continueBtn} onClick={handleConfirm}>
                Confirm
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
